import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  familyIconSrc,
  partyIconSrc,
  rapcoinIconSrc,
  swapsIconSrc,
} from '../../assets/transactions';
import { TRANSACTION_ICON_TYPES } from '../../services/transactionService';
import DownloadInvoiceButton from './DownloadInvoiceButton';
import TransactionStatusBadge from './TransactionStatusBadge';

const CARD_MAX_WIDTH = 745;

const PRODUCT_ICON_CONFIG = {
  [TRANSACTION_ICON_TYPES.party]: {
    src: partyIconSrc,
    sx: { width: '384%', left: '-143.19%', top: '-75.25%', height: '250.98%' },
  },
  [TRANSACTION_ICON_TYPES.family]: {
    src: familyIconSrc,
    sx: { width: '384%', left: '-23.25%', top: '-75.25%', height: '250.98%' },
  },
  [TRANSACTION_ICON_TYPES.corporate]: {
    src: partyIconSrc,
    sx: { width: '384%', left: '-268.3%', top: '-75.25%', height: '250.98%' },
  },
  [TRANSACTION_ICON_TYPES.swaps]: {
    src: swapsIconSrc,
    sx: { width: '353.1%', left: '-242.53%', top: '-61.61%', height: '235.4%' },
  },
  [TRANSACTION_ICON_TYPES.boost]: {
    src: swapsIconSrc,
    sx: { width: '353.1%', left: '-127.36%', top: '-61.61%', height: '235.4%' },
  },
  [TRANSACTION_ICON_TYPES.rapcoin]: {
    src: rapcoinIconSrc,
    sx: { width: '120.47%', left: '-9.65%', top: '-34.14%', height: '169.72%' },
  },
};

function resolveAmountColor(amountLabel = '') {
  if (amountLabel.startsWith('+')) return '#2ee437';
  return '#ff3b3b';
}

function LocalProductIcon({ iconType }) {
  const config = PRODUCT_ICON_CONFIG[iconType] || PRODUCT_ICON_CONFIG[TRANSACTION_ICON_TYPES.party];

  return (
    <Box
      sx={{
        position: 'relative',
        width: 24,
        height: 24,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={config.src}
        alt=""
        sx={{
          position: 'absolute',
          maxWidth: 'none',
          display: 'block',
          ...config.sx,
        }}
      />
    </Box>
  );
}

function ProductIcon({ iconType, iconUrl }) {
  const [remoteIconFailed, setRemoteIconFailed] = useState(false);

  if (iconUrl && !remoteIconFailed) {
    return (
      <Box
        component="img"
        src={iconUrl}
        alt=""
        onError={() => setRemoteIconFailed(true)}
        sx={{
          width: 24,
          height: 24,
          flexShrink: 0,
          objectFit: 'contain',
          display: 'block',
        }}
      />
    );
  }

  return <LocalProductIcon iconType={iconType} />;
}

/** Figma 11114:38488 / 38503 — transaction list card */
export default function TransactionCard({
  transaction,
  onDownloadInvoice,
  showDownloadInvoice = true,
}) {
  const showInvoice = showDownloadInvoice && Boolean(transaction?.showInvoiceButton);
  const amountColor = resolveAmountColor(transaction?.amountLabel);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: CARD_MAX_WIDTH,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '28px',
          border: '1.5px solid #df81ff',
          background: 'linear-gradient(180deg, #48003f 0%, #ad009c 100%)',
          overflow: 'hidden',
          minHeight: showInvoice ? 159 : 117,
          px: '20px',
          pt: '44px',
          pb: showInvoice ? '24px' : '20px',
        }}
      >
        <TransactionStatusBadge status={transaction?.status} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
            <ProductIcon iconType={transaction?.iconType} iconUrl={transaction?.iconUrl} />
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.8px',
                color: '#fff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {transaction?.title}
            </Typography>
          </Box>

          {transaction?.amountLabel ? (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 20,
                lineHeight: '32px',
                letterSpacing: '0.8px',
                color: amountColor,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {transaction.amountLabel}
            </Typography>
          ) : null}
        </Box>

        {transaction?.subtitle ? (
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: '20px',
              letterSpacing: '0.8px',
              color: '#fff',
              opacity: 0.7,
              whiteSpace: 'pre-wrap',
            }}
          >
            {transaction.subtitle}
          </Typography>
        ) : null}

        {showInvoice ? (
          <Box sx={{ mt: 1.25 }}>
            <DownloadInvoiceButton
              disabled={!transaction?.canDownloadInvoice}
              disabledTitle={transaction?.invoiceMessage}
              onClick={() => onDownloadInvoice?.(transaction)}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
